import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ThemeToggle from '../../components/ThemeToggle';

function MasterAdminSettings() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    grace_period_days: 3,
    late_fee_default: 10,
    suspension_days: 7
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const token = localStorage.getItem('masterAdminToken');
  const user = JSON.parse(localStorage.getItem('masterAdminUser') || '{}');

  useEffect(() => {
    if (!token) {
      navigate('/master-admin/login');
      return;
    }
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, navigate]);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/master-admin/platform-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem('masterAdminToken');
        navigate('/master-admin/login');
        return;
      }

      const data = await response.json();
      console.log('[MasterAdminSettings] API response:', data);
      if (!response.ok) throw new Error(data.error);

      // Parse settings - backend returns { key: { value, type, description } }
      const parsedSettings = {
        grace_period_days: data.grace_period_days?.value ?? 3,
        late_fee_default: data.late_fee_default?.value ?? 10,
        suspension_days: data.suspension_days?.value ?? 7
      };
      console.log('[MasterAdminSettings] Parsed settings:', parsedSettings);
      setSettings(parsedSettings);
    } catch (err) {
      console.error('[MasterAdminSettings] Fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    console.log('[MasterAdminSettings] Saving settings:', settings);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/master-admin/platform-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ settings })
      });

      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem('masterAdminToken');
        navigate('/master-admin/login');
        return;
      }

      const data = await response.json();
      console.log('[MasterAdminSettings] Save response:', data);
      if (!response.ok) throw new Error(data.error);

      setSuccess('Settings saved successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('[MasterAdminSettings] Save error:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('masterAdminToken');
    localStorage.removeItem('masterAdminUser');
    navigate('/master-admin/login');
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordSaving(true);
    setPasswordError('');
    setPasswordSuccess('');

    // Validate passwords match
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match');
      setPasswordSaving(false);
      return;
    }

    // Validate password length
    if (passwordData.newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      setPasswordSaving(false);
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/master-admin/change-password`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error);

      setPasswordSuccess('Password changed successfully!');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });

      // Auto-clear success message
      setTimeout(() => setPasswordSuccess(''), 5000);
    } catch (err) {
      setPasswordError(err.message || 'Failed to change password');
    } finally {
      setPasswordSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-page master-admin-page">
        <div className="admin-loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="admin-page master-admin-page">
      {/* Header */}
      <header className="admin-header">
        <div className="admin-header-left">
          <h1>DeedStack Platform Admin</h1>
        </div>
        <div className="admin-header-right">
          <span className="admin-user-name">{user.firstName} {user.lastName}</span>
          <ThemeToggle />
          <button onClick={handleLogout} className="btn btn-secondary btn-small">
            Logout
          </button>
        </div>
      </header>

      {/* Navigation */}
      <nav className="admin-nav">
        <Link to="/master-admin/dashboard" className="admin-nav-link">Dashboard</Link>
        <Link to="/master-admin/tenants" className="admin-nav-link">Tenants</Link>
        <Link to="/master-admin/calendar" className="admin-nav-link">Calendar</Link>
        <Link to="/master-admin/emails" className="admin-nav-link">Emails</Link>
        <Link to="/master-admin/billing" className="admin-nav-link">Billing</Link>
        <Link to="/master-admin/financials" className="admin-nav-link">Financials</Link>
        <Link to="/master-admin/settings" className="admin-nav-link active">Settings</Link>
      </nav>

      {/* Main Content */}
      <main className="admin-main">
        <div className="admin-container">
          <div className="admin-section">
            <h2>Platform Settings</h2>
            <p style={{ color: 'var(--color-text-light)', marginBottom: '2rem' }}>
              Configure billing and subscription settings that apply to all tenants.
            </p>

            {error && <div className="error-message" style={{ marginBottom: '1rem' }}>{error}</div>}
            {success && <div className="success-message" style={{ marginBottom: '1rem' }}>{success}</div>}

            <div className="admin-card">
              <h3>Billing Settings</h3>

              <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                <label htmlFor="grace_period_days" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Grace Period (Days)
                </label>
                <p style={{ color: 'var(--color-text-light)', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                  Days before late fee is applied after payment due date
                </p>
                <input
                  type="number"
                  id="grace_period_days"
                  min="0"
                  max="30"
                  value={settings.grace_period_days}
                  onChange={(e) => setSettings({ ...settings, grace_period_days: parseInt(e.target.value) || 0 })}
                  style={{
                    width: '120px',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: '1px solid var(--color-border)',
                    fontSize: '1rem'
                  }}
                />
                <span style={{ marginLeft: '10px', color: 'var(--color-text-light)' }}>days</span>
              </div>

              <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                <label htmlFor="late_fee_default" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Late Fee Amount
                </label>
                <p style={{ color: 'var(--color-text-light)', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                  Fee charged when payment is past the grace period
                </p>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ marginRight: '5px', fontSize: '1rem' }}>$</span>
                  <input
                    type="number"
                    id="late_fee_default"
                    min="0"
                    max="100"
                    step="0.01"
                    value={settings.late_fee_default}
                    onChange={(e) => setSettings({ ...settings, late_fee_default: parseFloat(e.target.value) || 0 })}
                    style={{
                      width: '120px',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1px solid var(--color-border)',
                      fontSize: '1rem'
                    }}
                  />
                </div>
              </div>

              <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                <label htmlFor="suspension_days" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Suspension Threshold (Days)
                </label>
                <p style={{ color: 'var(--color-text-light)', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                  Days past due before account is automatically suspended
                </p>
                <input
                  type="number"
                  id="suspension_days"
                  min="1"
                  max="60"
                  value={settings.suspension_days}
                  onChange={(e) => setSettings({ ...settings, suspension_days: parseInt(e.target.value) || 1 })}
                  style={{
                    width: '120px',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: '1px solid var(--color-border)',
                    fontSize: '1rem'
                  }}
                />
                <span style={{ marginLeft: '10px', color: 'var(--color-text-light)' }}>days</span>
              </div>

              <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid var(--color-border)' }}>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="btn btn-primary"
                  style={{ minWidth: '150px' }}
                >
                  {saving ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </div>

            {/* Info Card */}
            <div className="admin-card" style={{ marginTop: '1.5rem', background: 'var(--color-surface-sunken)' }}>
              <h4 style={{ margin: '0 0 1rem', color: 'var(--color-text-light)' }}>How These Settings Work</h4>
              <ul style={{ margin: 0, paddingLeft: '1.25rem', color: 'var(--color-text-light)', lineHeight: '1.8' }}>
                <li><strong>Grace Period:</strong> After a payment is due, tenants have this many days before a late fee is added.</li>
                <li><strong>Late Fee:</strong> Once the grace period expires, this fee is automatically added to the tenant's balance.</li>
                <li><strong>Suspension:</strong> If payment remains unpaid after this many days, the tenant's account is suspended and they lose admin access.</li>
              </ul>
            </div>

            {/* Change Password Section */}
            <div className="admin-card" style={{ marginTop: '2rem' }}>
              <h3 style={{ margin: '0 0 0.5rem' }}>Change Password</h3>
              <p style={{ color: 'var(--color-text-light)', marginBottom: '1.5rem' }}>
                Update your master admin account password.
              </p>

              {passwordError && (
                <div style={{
                  padding: '12px 16px',
                  backgroundColor: 'var(--color-error-bg)',
                  border: '1px solid var(--color-error-border)',
                  borderRadius: '8px',
                  color: 'var(--color-error-text)',
                  marginBottom: '1rem'
                }}>
                  {passwordError}
                </div>
              )}

              {passwordSuccess && (
                <div style={{
                  padding: '12px 16px',
                  backgroundColor: 'var(--color-success-bg)',
                  border: '1px solid var(--color-success-border)',
                  borderRadius: '8px',
                  color: 'var(--color-success-text)',
                  marginBottom: '1rem'
                }}>
                  {passwordSuccess}
                </div>
              )}

              <form onSubmit={handlePasswordChange}>
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label htmlFor="currentPassword" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                    Current Password *
                  </label>
                  <div style={{ position: 'relative', maxWidth: '400px' }}>
                    <input
                      type={showPasswords.current ? 'text' : 'password'}
                      id="currentPassword"
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '10px 44px 10px 12px',
                        borderRadius: '8px',
                        border: '1px solid var(--color-border)',
                        fontSize: '1rem'
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                      style={{
                        position: 'absolute',
                        right: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px 8px',
                        color: 'var(--color-text-light)',
                        fontSize: '1.1rem'
                      }}
                      title={showPasswords.current ? 'Hide password' : 'Show password'}
                    >
                      {showPasswords.current ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                          <line x1="1" y1="1" x2="23" y2="23"/>
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                          <circle cx="12" cy="12" r="3"/>
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label htmlFor="newPassword" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                    New Password *
                  </label>
                  <div style={{ position: 'relative', maxWidth: '400px' }}>
                    <input
                      type={showPasswords.new ? 'text' : 'password'}
                      id="newPassword"
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                      required
                      minLength="8"
                      style={{
                        width: '100%',
                        padding: '10px 44px 10px 12px',
                        borderRadius: '8px',
                        border: '1px solid var(--color-border)',
                        fontSize: '1rem'
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                      style={{
                        position: 'absolute',
                        right: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px 8px',
                        color: 'var(--color-text-light)',
                        fontSize: '1.1rem'
                      }}
                      title={showPasswords.new ? 'Hide password' : 'Show password'}
                    >
                      {showPasswords.new ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                          <line x1="1" y1="1" x2="23" y2="23"/>
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                          <circle cx="12" cy="12" r="3"/>
                        </svg>
                      )}
                    </button>
                  </div>
                  <small style={{ color: 'var(--color-text-light)', display: 'block', marginTop: '0.25rem' }}>
                    Minimum 8 characters
                  </small>
                </div>

                <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                  <label htmlFor="confirmPassword" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                    Confirm New Password *
                  </label>
                  <div style={{ position: 'relative', maxWidth: '400px' }}>
                    <input
                      type={showPasswords.confirm ? 'text' : 'password'}
                      id="confirmPassword"
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '10px 44px 10px 12px',
                        borderRadius: '8px',
                        border: '1px solid var(--color-border)',
                        fontSize: '1rem'
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                      style={{
                        position: 'absolute',
                        right: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px 8px',
                        color: 'var(--color-text-light)',
                        fontSize: '1.1rem'
                      }}
                      title={showPasswords.confirm ? 'Hide password' : 'Show password'}
                    >
                      {showPasswords.confirm ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                          <line x1="1" y1="1" x2="23" y2="23"/>
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                          <circle cx="12" cy="12" r="3"/>
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={passwordSaving}
                  className="btn btn-primary"
                  style={{ minWidth: '180px' }}
                >
                  {passwordSaving ? 'Changing...' : 'Change Password'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default MasterAdminSettings;
